import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PasswordField } from '@/components/Ui/Form/Rhf/PasswordField';
import { removeEmptyValues } from '@/helpers/utils';

import { resetPasswordDefaults, resetPasswordSchema, type ResetPasswordValues } from './ResetPasswordForm.schema';

const inputClasses = 'relative block w-full appearance-none rounded-md border border-input px-3 py-2 text-foreground placeholder-muted-foreground ring-offset-primary focus-visible:outline-none focus:z-10 focus-visible:ring-1 focus:outline-none focus-visible:ring-offset-1 sm:text-sm';

interface ResetPasswordFormProps {
    onCancel: () => void;
    onSubmit: (values: Partial<ResetPasswordValues>) => unknown;
}

function ResetPasswordForm({ onCancel, onSubmit }: ResetPasswordFormProps) {
    const [
        t,
    ] = useTranslation('pages');

    const form = useForm({
        resolver: zodResolver(resetPasswordSchema),
        defaultValues: resetPasswordDefaults(),
    });
    const { handleSubmit, formState: { errors } } = form;

    return (
        <div className='mb-5'>
            <FormProvider { ...form }>
                <form
                    autoComplete='off'
                    className='space-y-8 divide-y divide-border'
                    name='lastpass-disable-search'
                    onSubmit={ handleSubmit((values) => {
                        return onSubmit(removeEmptyValues(values));
                    }) }
                >
                    <div className='flex flex-col gap-4 items-top justify-top'>
                        <div>
                            <label className='block text-sm font-medium text-foreground mb-1' htmlFor='reset-password'>
                                {t('forms.password')}
                            </label>
                            <PasswordField
                                className={ inputClasses }
                                id='reset-password'
                                name='password'
                            />
                            {errors.password?.message && (
                                <p className='text-destructive text-xs mt-1'>{t(errors.password.message)}</p>
                            )}
                            <div className='bg-muted border shadow my-4 p-1'>
                                <div className='m-4 text-xs'>
                                    <div className='font-bold mb-2'>{t('Password requirements:')}</div>
                                    <ul>
                                        <li>{t('Minimum length of 8 characters.')}</li>
                                        <li>{t('Must contain a mix of upper and lower case letters.')}</li>
                                        <li>{t('Must include numbers.')}</li>
                                        <li>{t('Must have symbols.')}</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className='block text-sm font-medium text-foreground mb-1' htmlFor='reset-password-confirmation'>
                                {t('forms.password_confirmation')}
                            </label>
                            <PasswordField
                                className={ inputClasses }
                                id='reset-password-confirmation'
                                name='password_confirmation'
                            />
                            {errors.password_confirmation?.message && (
                                <p className='text-destructive text-xs mt-1'>{t(errors.password_confirmation.message)}</p>
                            )}
                        </div>
                    </div>
                    <div className='pt-5'>
                        <div className='flex justify-end'>
                            <button
                                className='rounded-md border border-input bg-background py-2 px-4 text-sm font-medium text-foreground shadow-sm hover:bg-muted focus:outline-none focus:ring-2 focus:ring-info focus:ring-offset-2'
                                onClick={ () => {
                                    return onCancel();
                                } }
                                type='button'
                            >
                                {t('forms.generic.cancel')}
                            </button>
                            <button
                                className='ml-3 inline-flex justify-center rounded-md border border-transparent bg-info py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-info focus:outline-none focus:ring-2 focus:ring-info focus:ring-offset-2'
                                type='submit'
                            >
                                {t('forms.generic.save')}
                            </button>
                        </div>
                    </div>
                </form>
            </FormProvider>
        </div>
    );
}

export default ResetPasswordForm;
